/**
 * Canonical timestamp helpers for the customer timeline.
 *
 * Task 3535c: the platform now distinguishes between when an event was
 * recorded (`created`) and when the underlying action actually happened
 * (`occurred_at`). For historic ingestions (e.g. a Reflectly subscription
 * restored from 2022), these two values can differ by years.
 *
 * All timeline display and sort logic should key on `occurred_at` — the
 * true time of the action — with a `created` fallback for rows that
 * pre-date the 3534a/3536 backfill or for any future null-slip through.
 *
 * Centralising the logic here means schema changes (renaming, tightening
 * nullability, etc.) become single-point edits.
 */

import type { PlatformEvent } from '../types'

// ── Constants ─────────────────────────────────────────────────────────────────

/**
 * Minimum ingest lag (in seconds) that is operationally interesting enough to
 * surface in the UI. Below this threshold, the divergence is just a Pub/Sub
 * hop — not indicative of a historic backdating. Above it, we show an
 * "ingest lag" disclosure so operators can see which events are historic
 * ingestions vs real-time.
 */
export const SIGNIFICANT_LAG_SECONDS = 60

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Return the canonical timeline timestamp for an event — i.e. when the
 * underlying action actually happened, in Unix seconds.
 *
 * Uses `occurred_at` with a `created` fallback for rows that pre-date the
 * 3536 backfill or for any row where `occurred_at` is unexpectedly absent.
 *
 * Returns 0 when both fields are absent (malformed / legacy payload) so
 * downstream formatters receive a value their own null-guards can handle
 * without risking NaN arithmetic in the sort comparator.
 */
export function timelineTimestamp(evt: PlatformEvent): number {
  return evt.occurred_at ?? evt.created ?? 0
}

/**
 * Lag in seconds between when we recorded the event (`created`) and when
 * the action actually happened (`occurred_at`).
 *
 * Positive → we recorded it after the fact (historic ingestion).
 * Negative → clock skew / impossible (treat as no lag).
 * Zero    → recorded at the same time (real-time event).
 *
 * Returns 0 when `occurred_at` is absent (no lag to measure).
 */
export function ingestLagSeconds(evt: PlatformEvent): number {
  if (evt.occurred_at == null) return 0
  return evt.created - evt.occurred_at
}

/**
 * True when the lag between `created` and `occurred_at` exceeds
 * `SIGNIFICANT_LAG_SECONDS`. Drives the "recorded N later" disclosure in
 * the event row / card.
 *
 * Using Math.abs to catch negative-lag (clock skew) cases too — either
 * direction of significant divergence is worth surfacing.
 */
export function hasSignificantLag(evt: PlatformEvent): boolean {
  return Math.abs(ingestLagSeconds(evt)) > SIGNIFICANT_LAG_SECONDS
}
